/**
 * useSequenceStore: port of vendor-ref/mara/app/ui/src/store/sequence.js with the Unity bridge replaced by local
 * state. The `sequences` / `sequence.selection` / `sequence.component` events the player pushed are computed here
 * from the Lite store (packages/state/src/sequence.ts); SetSequenceSelection, ClearSequenceSelection,
 * CreateComponentFromSequence, UpdateComponentFromSequence and ClearActiveComponent become store actions
 * (WebGlAPI.Sequences.cs, SequencesMenuController.cs). Hover also outlines the residues in 3D.
 */
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
import { VISUALIZATION_PRESETS } from '@nanome-lite/look';
import {
  activeComponentLabel, atomsFromSequenceSelection, buildSequenceEntry, selectionFilters, sequenceEntryKey, sequenceSelectionFromAtoms,
  useLiteStore, type SequenceEntry, type SequenceResidue, type VisualizationModel,
} from '@nanome-lite/state';

export interface SequenceHover { entryKey: string; kind: 'residue' | 'chain'; index?: number; chain: string; text: string }
export interface SequenceSliceState { selected: Set<number>; partial: Set<number> }

/**
 * The menu creates components with no visualization (SequencesMenuController.cs:43, an empty Visualization array); Lite
 * gives the new component Ball and Stick so it shows up straight away.
 */
export const SEQUENCE_COMPONENT_VISUALIZATION: VisualizationModel = { ...(VISUALIZATION_PRESETS.ballAndStick as unknown as VisualizationModel), visible: true };

const nextFrame = (fn: () => void): number => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16) as unknown as number);
const cancelFrame = (h: number) => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(h) : clearTimeout(h));

/** Make `set` exactly match `next`, touching it only where they differ (sequence.js syncSet). */
function syncSet(set: Set<number>, next: Iterable<number>) {
  const n = new Set(next);
  for (const v of set) if (!n.has(v)) set.delete(v);
  for (const v of n) if (!set.has(v)) set.add(v);
}

export const useSequenceStore = defineStore('sequence', () => {
  const lite = useLiteStore();

  // UI
  const isCollapsed = ref(true);
  // "pushed" state, computed locally
  const entries = shallowRef<SequenceEntry[]>([]);
  const selection = reactive(new Map<string, SequenceSliceState>());
  // local-only hover
  const hover = ref<SequenceHover | null>(null);
  /** last clicked residue, for shift-click range extension */
  const anchor = ref<{ entryKey: string; index: number } | null>(null);

  let commitHandle = 0;
  let suppressEcho = false;
  function setSuppressEcho(value: boolean) { suppressEcho = value; }

  // ---- getters ----
  const hasSelection = computed(() => {
    for (const s of selection.values()) if (s.selected.size || s.partial.size) return true;
    return false;
  });
  const readout = computed(() => hover.value?.text ?? '');
  const entryByKey = (key: string) => entries.value.find(e => sequenceEntryKey(e) === key);

  /** sequence.component (WebGlAPI SequenceComponentState): the component 'Edit selection' loaded, if any. */
  const component = computed(() => {
    const serial = lite.activeComponentSerial;
    const c = serial === null ? undefined : lite.componentBySerial(serial);
    if (!c) return { serial: -1, name: '', mode: 'create' as const };
    const names = Object.keys(lite.selection.byEntry).map(Number).sort((a, b) => a - b)
      .map(s => lite.entryBySerial(s)?.name).filter((n): n is string => !!n);
    return { serial: c.serial, name: activeComponentLabel(names, c.name), mode: 'update' as const };
  });

  // ---- selection model ----
  function slice(key: string): SequenceSliceState {
    let s = selection.get(key);
    if (!s) { selection.set(key, { selected: new Set(), partial: new Set() }); s = selection.get(key)!; }
    return s;
  }

  function applyGesture(key: string, selected: Iterable<number>, partial: Iterable<number>) {
    const s = slice(key);
    syncSet(s.selected, selected);
    syncSet(s.partial, partial);
  }

  function selectResidues(key: string, indices: number[]) {
    const s = slice(key);
    for (const i of indices) { s.selected.add(i); s.partial.delete(i); }
  }

  function deselectResidues(key: string, indices: number[]) {
    const s = slice(key);
    for (const i of indices) { s.selected.delete(i); s.partial.delete(i); }
  }

  /** The atoms the sequence selection stands for, per entry (SetSequenceSelection -> TryResolveSelection). */
  function selectionAtoms(): Record<number, number[]> {
    const byEntry: Record<number, number[]> = { ...lite.selection.byEntry };
    for (const e of entries.value) {
      const model = lite.models.get(e.entrySerial);
      if (!model) continue;
      const s = selection.get(sequenceEntryKey(e));
      const atoms = s ? atomsFromSequenceSelection(e, model, s.selected, [...s.partial].filter(i => !s.selected.has(i)), lite.selection.byEntry[e.entrySerial] ?? []) : [];
      if (atoms.length) byEntry[e.entrySerial] = atoms; else delete byEntry[e.entrySerial];
    }
    return byEntry;
  }

  /** One undo step for the next selection change (a drag, a chain click). */
  function beginEdit() { lite.commit(); }

  /** SetSequenceSelection: replace the whole 3D selection, debounced one frame like the bridge call. */
  function commit() {
    if (commitHandle) cancelFrame(commitHandle);
    commitHandle = nextFrame(() => { commitHandle = 0; flush(); });
  }
  function flush() {
    if (commitHandle) { cancelFrame(commitHandle); commitHandle = 0; }
    lite.replaceSelection(selectionAtoms(), { commit: false });
  }

  /** ClearSequenceSelection: drop the active component and the selection. */
  function clearSelection() {
    selection.clear();
    anchor.value = null;
    lite.clearActiveComponent();
    lite.clearSelection();
  }

  function perEntryAtoms() {
    const byEntry = selectionAtoms();
    return entries.value
      .filter(e => byEntry[e.entrySerial]?.length)
      .map(e => ({ entrySerial: e.entrySerial, model: lite.models.get(e.entrySerial)!, atoms: byEntry[e.entrySerial]! }));
  }

  /** CreateComponentFromSequence: SelectionFilterBuilder filters through the store (undoable, new serial). */
  function createComponent(name: string) {
    const per = perEntryAtoms();
    if (!per.length || !name.trim()) return null;
    const filters = selectionFilters(per);
    if (!filters.length) return null;
    return lite.addComponent({ name: name.trim(), filters, visualizations: [{ ...SEQUENCE_COMPONENT_VISUALIZATION }] });
  }

  /** UpdateComponentFromSequence: same serial, new filters. */
  function updateComponent() {
    const serial = component.value.serial;
    const per = perEntryAtoms();
    if (serial < 0 || !per.length) return;
    lite.updateComponentFilters(serial, selectionFilters(per));
  }

  function clearActiveComponent() { lite.clearActiveComponent(); }

  // ---- hover (local, plus the 3D outline) ----
  function highlight(entry: SequenceEntry | null, residues: SequenceResidue[]) {
    const indices = residues.filter(r => r.residueIndex >= 0).map(r => r.residueIndex);
    lite.engine?.highlightResidues?.(entry?.entrySerial ?? 0, entry && indices.length ? indices : null);
  }

  function setHoverResidue(entry: SequenceEntry, residue: SequenceResidue) {
    if (residue.missing) { clearHover(); return; }
    const key = sequenceEntryKey(entry);
    const h = hover.value;
    if (h?.kind === 'residue' && h.entryKey === key && h.index === residue.index) return;
    hover.value = { entryKey: key, kind: 'residue', index: residue.index, chain: residue.chain, text: `${entry.entryName} ${residue.chain || '_'}: ${residue.name || residue.code} ${residue.serial}` };
    highlight(entry, [residue]);
  }

  function setHoverChain(entry: SequenceEntry, chain: string) {
    const key = sequenceEntryKey(entry);
    const h = hover.value;
    if (h?.kind === 'chain' && h.entryKey === key && h.chain === chain) return;
    hover.value = { entryKey: key, kind: 'chain', chain, text: `${entry.entryName} ${chain || '_'}` };
    highlight(entry, entry.residues.filter(r => !r.missing && r.chain === chain));
  }

  function clearHover() {
    if (!hover.value) return;
    hover.value = null;
    highlight(null, []);
  }

  // ---- "inbound": sequences and selection derived from the Lite store ----
  function rebuild() {
    entries.value = lite.entries
      .filter(e => lite.models.has(e.serial))
      .map(e => buildSequenceEntry(e, lite.models.get(e.serial)!));
    const live = new Map(entries.value.map(e => [sequenceEntryKey(e), e.residues.length]));
    for (const key of [...selection.keys()]) {
      const len = live.get(key);
      if (len === undefined) { selection.delete(key); continue; }
      const s = selection.get(key)!;
      for (const i of [...s.selected]) if (i >= len) s.selected.delete(i);
      for (const i of [...s.partial]) if (i >= len) s.partial.delete(i);
    }
    if (anchor.value && !live.has(anchor.value.entryKey)) anchor.value = null;
    if (hover.value && !live.has(hover.value.entryKey)) clearHover();
    syncFromStore();
  }

  /** sequence.selection: residues fully / partly covered by the 3D selection. */
  function syncFromStore() {
    if (suppressEcho || isCollapsed.value) return;
    const present = new Set<string>();
    for (const e of entries.value) {
      const model = lite.models.get(e.entrySerial);
      const atoms = lite.selection.byEntry[e.entrySerial];
      if (!model || !atoms?.length) continue;
      const key = sequenceEntryKey(e);
      present.add(key);
      const { selected, partial } = sequenceSelectionFromAtoms(e, model, atoms);
      applyGesture(key, selected, partial);
    }
    for (const key of [...selection.keys()]) if (!present.has(key)) selection.delete(key);
  }

  // Data only flows while the menu is open (sequence.js: SetSequenceMenuOpen gates the player's pushes).
  watch(
    () => isCollapsed.value ? '' : lite.entries.map(e => `${e.serial}:${e.currentModelSerial}:${e.name}:${lite.models.get(e.serial) ? 1 : 0}`).join('|'),
    (sig) => { if (sig || !isCollapsed.value) rebuild(); else { entries.value = []; selection.clear(); } },
    { immediate: true },
  );
  watch(isCollapsed, (c) => { if (c) clearHover(); });
  watch(() => lite.selection, () => syncFromStore(), { deep: true });

  return {
    // state
    isCollapsed, entries, selection, component, hover, anchor,
    // getters
    hasSelection, readout, entryByKey,
    // selection model
    slice, applyGesture, selectResidues, deselectResidues, setSuppressEcho, beginEdit,
    // hover
    setHoverResidue, setHoverChain, clearHover,
    // actions
    commit, flush, clearSelection, createComponent, updateComponent, clearActiveComponent, rebuild,
  };
});
